import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TierBadge, RiskBadge, PocChip, PocChipPair } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CaretRight, Trophy, Crown, Medal, Lightning } from "@phosphor-icons/react";
import ExportButton from "@/components/ExportButton";

const POWER_USER_COLUMNS_EXT = [
  { key: "rank", label: "Rank" },
  { key: "firm_name", label: "Firm name" },
  { key: "subdomain", label: "Subdomain" },
  { key: "tier", label: "Tier" },
  { key: "poc", label: "POC" },
  { key: "city", label: "City" },
  { key: "num_users", label: "No. of Users" },
  { key: "import_books", label: "Import Books" },
  { key: "ledger_classification", label: "Ledger Classification" },
  { key: "statement_generation", label: "Statement Generation" },
  { key: "ledger_scrutiny", label: "Ledger Scrutiny" },
  { key: "issues_raised_90d", label: "Issues Raised (90d)" },
  { key: "total_clients", label: "Total Clients" },
  { key: "total_assignments", label: "Total Assignments" },
  { key: "total_open_assignments", label: "Total Open Assignments" },
  { key: "total_ledgers", label: "Total Ledgers" },
  { key: "total_vouchers", label: "Total Vouchers" },
  { key: "events_in_period", label: "Events in period" },
  { key: "last_seen", label: "Last seen (UTC)" },
];

const PERIOD_LABELS = {
  "7": "Last 7 days",
  "30": "Last 30 days",
  "90": "Last 90 days",
  "180": "Last 180 days",
  "365": "Last 365 days",
};

export default function PowerUsers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPoc = user?.role === "poc";
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get("period") || "30";
  const tier = searchParams.get("tier") || "all";
  const poc = searchParams.get("poc") || "all";
  const limit = parseInt(searchParams.get("limit") || "10", 10);

  const setParam = (k, v) => {
    const n = new URLSearchParams(searchParams);
    if (!v || v === "all") n.delete(k);
    else n.set(k, v);
    setSearchParams(n, { replace: false });
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pocOptions, setPocOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);

  useEffect(() => {
    api.get("/meta/options").then((r) => {
      setPocOptions(r.data.pocs);
      setTierOptions(r.data.tiers);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/customers/power-users", {
        params: {
          period,
          limit,
          tier: tier !== "all" ? tier : undefined,
          poc: poc !== "all" ? poc : undefined,
        },
      })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [period, tier, poc, limit]);

  const maxEvents = useMemo(() => {
    if (!data?.top?.length) return 1;
    return data.top[0].events_in_period || 1;
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
            <Trophy size={28} weight="fill" className="text-amber-500" />
            Power User Benchmark
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Top firms by Mixpanel event volume. Use them as the reference benchmark
            for healthy adoption — and as case studies for the rest of the book.
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 items-center" data-testid="pu-filter-bar">
        <Select value={period} onValueChange={(v) => setParam("period", v)}>
          <SelectTrigger className="w-[180px]" data-testid="pu-filter-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PERIOD_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tier} onValueChange={(v) => setParam("tier", v)}>
          <SelectTrigger className="w-[150px]" data-testid="pu-filter-tier">
            <SelectValue placeholder="All tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {tierOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={poc} onValueChange={(v) => setParam("poc", v)}>
          <SelectTrigger className="w-[160px]" data-testid="pu-filter-poc">
            <SelectValue placeholder="All POCs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All POCs</SelectItem>
            {pocOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={(v) => setParam("limit", v)}>
          <SelectTrigger className="w-[120px]" data-testid="pu-filter-limit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">Top 5</SelectItem>
            <SelectItem value="10">Top 10</SelectItem>
            <SelectItem value="25">Top 25</SelectItem>
            <SelectItem value="50">Top 50</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="pu-summary">
        <SummaryStat label="Top firm" value={data?.top?.[0]?.firm_name || "—"} hint={`${data?.top?.[0]?.events_in_period?.toLocaleString() || 0} events`} icon={Crown} color="text-amber-500" />
        <SummaryStat label="Top-N concentration" value={`${data?.concentration_pct ?? 0}%`} hint={`of all events from ${limit} firms`} icon={Medal} color="text-violet-500" />
        <SummaryStat label="Active firms in period" value={data?.total_active_firms ?? 0} hint={`${(data?.total_events_in_period ?? 0).toLocaleString()} total events`} icon={Lightning} color="text-blue-500" />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="pu-leaderboard">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-semibold text-gray-900">
              Leaderboard · {PERIOD_LABELS[period]}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Click a row to open the firm 360° view.
            </div>
          </div>
          <ExportButton
            filename={`power-users-${period}`}
            sheetName="Power Users"
            columns={POWER_USER_COLUMNS_EXT}
            rows={(data?.top || []).map((r, idx) => ({
              rank: idx + 1,
              firm_name: r.firm_name,
              subdomain: r.subdomain,
              tier: r.tier,
              poc: r.poc,
              city: r.city,
              num_users: r.num_users || "-",
              import_books: r.import_books || 0,
              ledger_classification: r.ledger_classification || 0,
              statement_generation: r.statement_generation || 0,
              ledger_scrutiny: r.ledger_scrutiny || 0,
              issues_raised_90d: r.issues_raised_90d || 0,
              total_clients: r.total_clients || 0,
              total_assignments: r.total_assignments || 0,
              total_open_assignments: r.total_open_assignments || 0,
              total_ledgers: r.total_ledgers || 0,
              total_vouchers: r.total_vouchers || 0,
              events_in_period: r.events_in_period,
              last_seen: r.last_seen,
            }))}
            testId="export-power-users"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left">
                <Th className="w-12">#</Th>
                <Th>Firm</Th>
                <Th>Tier</Th>
                <Th>Risk</Th>
                <Th>POC</Th>
                <Th className="text-right">Events</Th>
                <Th className="text-right">IB</Th>
                <Th className="text-right">LC</Th>
                <Th className="text-right">SG</Th>
                <Th className="text-right">LS</Th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-sm text-gray-400">Loading…</td></tr>
              )}
              {!loading && data && data.top.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-sm text-gray-400">No active firms in this window with the current filters.</td></tr>
              )}
              {!loading && data && data.top.map((f, idx) => {
                const barPct = (f.events_in_period / maxEvents) * 100;
                return (
                  <tr
                    key={f.subdomain}
                    onClick={() => navigate(`/firms/${f.subdomain}${firmFromQS(location)}`)}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                    data-testid={`pu-row-${f.subdomain}`}
                  >
                    <td className="px-4 py-3">
                      <RankBadge rank={idx + 1} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{f.firm_name}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 font-mono">
                        {f.firm_url || `${f.subdomain}.assureai.in`}
                      </div>
                    </td>
                    <td className="px-4 py-3"><TierBadge tier={f.tier} /></td>
                    <td className="px-4 py-3"><RiskBadge risk={f.risk} /></td>
                    <td className="px-4 py-3"><PocChipPair poc={f.poc} salesPoc={f.sales_poc} /></td>
                    <td className="px-4 py-3 min-w-[180px]">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
                          <div className="h-full bg-amber-500" style={{ width: `${barPct}%` }} />
                        </div>
                        <span className="font-mono tabular-nums text-sm font-semibold text-gray-900 min-w-[60px] text-right">
                          {f.events_in_period.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-gray-700">{f.import_books.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-gray-700">{f.ledger_classification.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-700 font-semibold">{f.statement_generation.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-gray-700">{f.ledger_scrutiny.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right"><CaretRight size={16} className="text-gray-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400 font-mono">
          IB = Import Books · LC = Ledger Classification · SG = Final Statement Generation · LS = Ledger Scrutiny
        </div>
      </div>
    </div>
  );
}

function RankBadge({ rank }) {
  const colors =
    rank === 1 ? "bg-amber-100 text-amber-700 border-amber-200" :
    rank === 2 ? "bg-gray-100 text-gray-700 border-gray-300" :
    rank === 3 ? "bg-orange-100 text-orange-700 border-orange-200" :
    "bg-gray-50 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-mono font-semibold border ${colors}`}>
      {rank}
    </span>
  );
}

function SummaryStat({ label, value, hint, icon: Icon, color }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} weight="fill" className={color} />}
        <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-medium">
          {label}
        </div>
      </div>
      <div className="mt-2 font-display text-[24px] leading-tight font-semibold text-gray-900 truncate">
        {value}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500 ${className}`}>{children}</th>;
}
